import React, { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Reports from '../../../services/reports';
import { DIMENSIONS_OPTIONS, FILTERS_OPTIONS, Dimensions, MetricsConfig, MetricType } from '../../consts';
import { isObjectEmpty } from '../../../utils/general';
import { useAsync } from '../../../utils/customHooks';
import { createCohortOptions } from '../../../creativeGallery/metricsUtils';
import fieldVisibilityRules from '../../../selectors/reportsConfig/fieldVisibilityRules';
import { getAdminModeEnabled, getUserData } from '../../../selectors/user';
import Section from '../Section/Section';
import PeriodPicker from '../PeriodPicker/PeriodPicker';
import MetricsSection from '../MetricDropdown/MetricsSection';
import FiltersSection from './FiltersSection';
import DimensionsSection from './DimensionsSection';
import css from './Filters.css';

const reportsService = new Reports();

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS.FILTERS';
const DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_PERIOD_DAYS = 7;

const toOption = key => ({ value: key, label: `STATIC.${key}` });

const getFilteredMetricsOptions = availableCohortOptionsSet => {
    const entries = Object.entries(MetricsConfig).filter(
        ([, { apiType, apiName, period }]) =>
            apiType !== 'cohort' || availableCohortOptionsSet.has(`${apiName}_${period}`)
    );

    return entries.reduce(
        (acc, [key, { types }]) => {
            if (types.includes(MetricType.COLOR)) acc.color.push(toOption(key));
            if (types.includes(MetricType.SIZE)) acc.size.push(toOption(key));
            return acc;
        },
        { color: [], size: [] }
    );
};

const buildPeriodRange = (endDate, days = DEFAULT_PERIOD_DAYS) => {
    const end = moment(endDate);
    const start = moment(end).subtract(days - 1, 'days');
    return { start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT) };
};

const buildComparisonPeriod = firstPeriod => {
    const days = moment(firstPeriod.end).diff(moment(firstPeriod.start), 'days') + 1;
    const compEnd = moment(firstPeriod.start).subtract(1, 'days');
    return buildPeriodRange(compEnd, days);
};

const getSelectedOption = (options, value) => options.find(option => option.value === value);

const resolveMetricFromUrl = key => (key && MetricsConfig[key] ? toOption(key) : null);

const resolveOptionsFromUrl = (urlValues, options) => {
    if (!urlValues) return null;
    return urlValues.map(value => getSelectedOption(options, value)).filter(Boolean);
};

const DEFAULT_DIMENSIONS = [Dimensions.APP, Dimensions.SOURCE];

const Filters = ({ onRun = () => {}, initialUrlParams }) => {
    const filtersRef = useRef({
        dimensions: DEFAULT_DIMENSIONS.map(value => getSelectedOption(DIMENSIONS_OPTIONS, value)),
        colorMetric: null,
        sizeMetric: null,
        filters: [],
        firstPeriod: buildPeriodRange(),
    });

    const [showComparison, setShowComparison] = useState(false);
    const [firstPeriod, setFirstPeriod] = useState(() => initialUrlParams?.firstPeriod || buildPeriodRange());
    const comparisonPeriod = useMemo(() => buildComparisonPeriod(firstPeriod), [firstPeriod]);

    const isAdmin = useSelector(getAdminModeEnabled);
    const userData = useSelector(state => getUserData(state));
    const fieldsRule = useCallback(field => fieldVisibilityRules.reports(field, isAdmin, userData), [
        isAdmin,
        userData,
    ]);

    const [{ data: cohortData }, fetchCohortData] = useAsync();

    useEffect(() => {
        fetchCohortData(reportsService.getCohortDataAndConversionEvents());
    }, [fetchCohortData]);

    const { color: COLOR_METRICS_OPTIONS, size: SIZE_METRICS_OPTIONS } = useMemo(() => {
        if (!cohortData) return { color: [], size: [] };
        const cohortMetrics = cohortData?.cohortMetrics.filter(fieldsRule);
        const cohortPeriods = cohortData?.periods.filter(fieldsRule);
        const availableSet = new Set(createCohortOptions(cohortMetrics, cohortPeriods).map(({ value }) => value));
        return getFilteredMetricsOptions(availableSet);
    }, [cohortData, fieldsRule]);

    const tryRun = useCallback(() => {
        const ref = filtersRef.current;
        if (
            !ref.dimensions?.length ||
            !ref.colorMetric ||
            !ref.sizeMetric ||
            !ref.firstPeriod?.start ||
            !ref.firstPeriod?.end
        )
            return;
        const snapshot = { ...ref, secondPeriod: buildComparisonPeriod(ref.firstPeriod) };
        onRun(snapshot);
    }, [onRun]);

    const [{ resolvedColor: initialColorMetric, resolvedSize: initialSizeMetric }] = useState(() => {
        if (!initialUrlParams || isObjectEmpty(initialUrlParams)) return {};

        const resolvedDimensions = resolveOptionsFromUrl(initialUrlParams.dimensions, DIMENSIONS_OPTIONS);
        if (resolvedDimensions?.length) filtersRef.current.dimensions = resolvedDimensions;

        if (initialUrlParams.filters) {
            const validTypes = new Set(FILTERS_OPTIONS.map(option => option.value));
            filtersRef.current.filters = initialUrlParams.filters.filter(f => validTypes.has(f.type));
        }

        if (initialUrlParams.firstPeriod) filtersRef.current.firstPeriod = initialUrlParams.firstPeriod;

        const resolvedColor = resolveMetricFromUrl(initialUrlParams.colorMetric);
        const resolvedSize = resolveMetricFromUrl(initialUrlParams.sizeMetric);
        filtersRef.current.colorMetric = resolvedColor;
        filtersRef.current.sizeMetric = resolvedSize;

        return { resolvedColor, resolvedSize };
    });

    useEffect(() => {
        if (initialColorMetric) setShowComparison(true);
    }, []);

    // Auto-run after URL params are resolved
    useEffect(() => {
        if (initialColorMetric && initialSizeMetric) {
            tryRun();
        }
    }, [initialColorMetric, initialSizeMetric, tryRun]);

    const handleColorMetricChange = useCallback(
        metric => {
            setShowComparison(!!metric);
            tryRun();
        },
        [tryRun]
    );

    const handleSizeMetricChange = useCallback(() => {
        tryRun();
    }, [tryRun]);

    const handlePeriodChange = useCallback(
        data => {
            const newPeriod = {
                start: moment(data.start_date).format(DATE_FORMAT),
                end: moment(data.end_date).format(DATE_FORMAT),
            };
            setFirstPeriod(newPeriod);
            filtersRef.current.firstPeriod = newPeriod;
            tryRun();
        },
        [tryRun]
    );

    return (
        <div className={css.filters}>
            <div className={css.scrollable}>
                <FiltersSection filtersRef={filtersRef} onChange={tryRun} />

                <Section icon="calendar" labelKey={`${TRANSLATION_PREFIX}.ANALYSIS_PERIOD`}>
                    <PeriodPicker
                        id="anomaly-first-period"
                        start={firstPeriod.start}
                        end={firstPeriod.end}
                        onChange={handlePeriodChange}
                    />
                </Section>

                <DimensionsSection filtersRef={filtersRef} onChange={tryRun} />

                <MetricsSection
                    sizeOptions={SIZE_METRICS_OPTIONS}
                    colorOptions={COLOR_METRICS_OPTIONS}
                    filtersRef={filtersRef}
                    endDate={firstPeriod.end}
                    onSizeChange={handleSizeMetricChange}
                    onColorChange={handleColorMetricChange}
                />

                {showComparison && (
                    <Section icon="calendar" labelKey={`${TRANSLATION_PREFIX}.COMPARISON_PERIOD`}>
                        <PeriodPicker
                            id="anomaly-comparison-period"
                            start={comparisonPeriod.start}
                            end={comparisonPeriod.end}
                            disabled
                            helperText={`${TRANSLATION_PREFIX}.COMPARISON_PERIOD_HELPER`}
                        />
                    </Section>
                )}
            </div>
        </div>
    );
};

Filters.propTypes = {
    onRun: PropTypes.func,
    initialUrlParams: PropTypes.shape({}),
};

export default memo(Filters);
